<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRemoteConfigStore } from '@/stores/remoteConfig'
import { useAuthStore } from '@/stores/auth'
import { usePaymentMethodStore } from '@/stores/paymentMethod'
import { app } from '@/firebase'
import { getInstallations, getId, getToken } from 'firebase/installations'

const remoteConfigStore = useRemoteConfigStore()
const authStore = useAuthStore()
const paymentMethodStore = usePaymentMethodStore()

const isRefreshing = ref(false)
const fid = ref<string | null>(null)
const fisTokenStatus = ref<'loading' | 'success' | 'error'>('loading')
const configValues = ref<Record<string, string>>({})
const valueSources = ref<Record<string, 'remote' | 'default' | 'static'>>({})
const remoteConfigAppOptions = ref<{ projectId: string; appId: string; apiKey?: string } | null>(
  null,
)

async function loadRemoteConfigData() {
  try {
    remoteConfigAppOptions.value = await remoteConfigStore.getAppOptions()
    const values = await remoteConfigStore.getAllValues()
    configValues.value = values
    const sources: Record<string, 'remote' | 'default' | 'static'> = {}
    for (const key of Object.keys(values)) {
      sources[key] = await remoteConfigStore.getValueSource(key)
    }
    valueSources.value = sources
  } catch (err) {
    console.error('Failed to load Remote Config data for debug', err)
  }
}

// Get FID and Remote Config data on mount
onMounted(async () => {
  try {
    const installations = getInstallations(app)
    fid.value = await getId(installations)

    // Check token
    try {
      await getToken(installations)
      fisTokenStatus.value = 'success'
    } catch (err) {
      console.error('Failed to get FIS token for debug console', err)
      fisTokenStatus.value = 'error'
    }
  } catch (err) {
    console.error('Failed to get FID for debug console', err)
  }
  await loadRemoteConfigData()
})

// Get status info
const status = computed(() => {
  const opts = remoteConfigAppOptions.value
  return {
    isActivated: remoteConfigStore.isActivated,
    isLoading: remoteConfigStore.isLoading,
    error: remoteConfigStore.error,
    lastFetchTime: remoteConfigStore.lastFetchTime,
    projectId: opts?.projectId ?? '—',
    appId: opts?.appId ?? '—',
    apiKey: opts?.apiKey != null ? opts.apiKey.substring(0, 10) + '...' : '—',
    fid: fid.value,
    fisTokenStatus: fisTokenStatus.value,
  }
})

// Get user info for targeting
const userInfo = computed(() => {
  if (!authStore.user) {
    return null
  }

  return {
    uid: authStore.user.uid,
    email: authStore.user.email,
    isAdmin: authStore.isAdmin,
    isInstructor: authStore.isInstructor,
    isCoach: authStore.isCoach,
    isTestCustomer: authStore.isTestCustomer,
    subscriptionStatus: paymentMethodStore.subscriptionStatus,
    userRole: authStore.userRole,
  }
})

// Get value source for each key (from preloaded valueSources)
function getValueSource(key: string): 'remote' | 'default' | 'static' {
  return valueSources.value[key] ?? 'default'
}

// Refresh config - force refreshes from server, bypassing cache
async function refreshConfig() {
  if (!authStore.user) {
    alert('Please sign in first to fetch Remote Config')
    return
  }

  isRefreshing.value = true
  try {
    // Force refresh bypasses cache and fetches fresh data from Firebase
    await remoteConfigStore.fetchConfig(true)
    await loadRemoteConfigData()
  } catch (error) {
    console.error('Failed to refresh Remote Config:', error)
  } finally {
    isRefreshing.value = false
  }
}

// Format date
function formatDate(date: Date | null): string {
  if (!date) return 'Never'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

// Get source badge color
function getSourceColor(source: 'remote' | 'default' | 'static'): string {
  switch (source) {
    case 'remote':
      return 'bg-green-100 text-green-800'
    case 'default':
      return 'bg-gray-100 text-gray-800'
    case 'static':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}
</script>

<template>
  <div class="remote-config-debug">
    <!-- Status Section -->
    <div class="config-section">
      <h4 class="section-title">Status</h4>
      <div class="status-grid">
        <div class="status-item">
          <span class="status-label">Activated:</span>
          <span :class="['status-value', status.isActivated ? 'text-green-600' : 'text-red-600']">
            {{ status.isActivated ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Loading:</span>
          <span :class="['status-value', status.isLoading ? 'text-yellow-600' : 'text-gray-600']">
            {{ status.isLoading ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Last Fetch:</span>
          <span class="status-value text-gray-600">
            {{ formatDate(status.lastFetchTime) }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Project ID:</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.projectId }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">App ID:</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.appId }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">API Key:</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.apiKey }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Installation ID (FID):</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.fid || 'Loading...' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">FIS Token Status:</span>
          <span
            :class="[
              'status-value',
              status.fisTokenStatus === 'success' ? 'text-green-600' : 'text-red-600',
            ]"
          >
            {{
              status.fisTokenStatus === 'success'
                ? 'Valid'
                : status.fisTokenStatus === 'loading'
                  ? 'Checking...'
                  : 'Invalid/Missing'
            }}
          </span>
        </div>
      </div>

      <div v-if="status.error" class="error-box">
        <strong>Error:</strong> {{ status.error }}
        <div
          v-if="
            status.error.includes('400') ||
            status.error.includes('INVALID_ARGUMENT') ||
            status.fisTokenStatus === 'error'
          "
          class="mt-2 text-xs"
        >
          <p class="font-bold">Diagnostic Hint:</p>
          <div v-if="status.fisTokenStatus === 'error'">
            <p class="text-red-600 font-bold">The Firebase Installations (FIS) Token is invalid.</p>
            <p>
              This is likely the cause of the 400 error. Please ensure the
              <strong>Firebase Installations API</strong> is enabled in Google Cloud Console.
            </p>
          </div>
          <div v-else>
            <p>
              This usually means the <strong>Firebase Remote Config API</strong> is not enabled for
              this project, or there is an API key restriction.
            </p>
          </div>
          <a
            :href="
              'https://console.cloud.google.com/apis/library/firebaseremoteconfig.googleapis.com?project=' +
              status.projectId
            "
            target="_blank"
            class="text-blue-600 underline mt-1 block"
          >
            Enable Remote Config API in Google Cloud Console →
          </a>
          <a
            :href="
              'https://console.cloud.google.com/apis/library/firebaseinstallations.googleapis.com?project=' +
              status.projectId
            "
            target="_blank"
            class="text-blue-600 underline mt-1 block"
          >
            Enable Firebase Installations API in Google Cloud Console →
          </a>
        </div>
      </div>
    </div>

    <!-- User Info Section -->
    <div v-if="userInfo" class="config-section">
      <h4 class="section-title">User Info (for Targeting)</h4>
      <div class="user-info-grid">
        <div class="info-item">
          <span class="info-label">UID:</span>
          <span class="info-value font-mono text-xs">{{ userInfo.uid }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Email:</span>
          <span class="info-value">{{ userInfo.email || 'N/A' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Role:</span>
          <span class="info-value">{{ userInfo.userRole || 'N/A' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Admin:</span>
          <span class="info-value">{{ userInfo.isAdmin ? 'Yes' : 'No' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Instructor:</span>
          <span class="info-value">{{ userInfo.isInstructor ? 'Yes' : 'No' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Coach:</span>
          <span class="info-value">{{ userInfo.isCoach ? 'Yes' : 'No' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Test Customer:</span>
          <span class="info-value">{{ userInfo.isTestCustomer ? 'Yes' : 'No' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Subscription:</span>
          <span class="info-value">{{ userInfo.subscriptionStatus || 'N/A' }}</span>
        </div>
      </div>
    </div>

    <!-- Refresh Button -->
    <div class="config-section">
      <button
        @click="refreshConfig"
        :disabled="isRefreshing || status.isLoading"
        class="refresh-button"
      >
        <svg
          v-if="isRefreshing || status.isLoading"
          class="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          ></path>
        </svg>
        {{ isRefreshing || status.isLoading ? 'Refreshing...' : 'Refresh Config' }}
      </button>
    </div>

    <!-- Config Values Section -->
    <div class="config-section">
      <h4 class="section-title">
        Remote Config Values
        <span class="value-count">({{ Object.keys(configValues).length }})</span>
      </h4>

      <div v-if="Object.keys(configValues).length === 0" class="empty-state">
        <p>No Remote Config values found.</p>
        <p class="text-sm text-gray-500 mt-2">Add parameters in Firebase Console → Remote Config</p>
      </div>

      <div v-else class="config-values">
        <div v-for="[key, value] in Object.entries(configValues)" :key="key" class="config-item">
          <div class="config-key-row">
            <span class="config-key font-mono font-semibold">{{ key }}</span>
            <span :class="['source-badge', getSourceColor(getValueSource(key))]">
              {{ getValueSource(key) }}
            </span>
          </div>
          <div class="config-value">
            <span class="font-mono text-sm">{{ value }}</span>
            <span class="value-type">{{
              typeof value === 'boolean' ? 'boolean' : typeof value
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.remote-config-debug {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.config-section {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-count {
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 14px;
  font-weight: 500;
}

.error-box {
  margin-top: 12px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 14px;
}

.user-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: #111827;
}

.refresh-button {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.config-values {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.config-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.config-key-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.config-key {
  font-size: 14px;
  color: #111827;
}

.source-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.config-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
}

.value-type {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}
</style>
